import { HttpClient } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'

import { EventSourcePolyfill } from 'event-source-polyfill/src/eventsource'

import { Observable } from 'rxjs'

import { aiStreamHeaders } from '@flowx/designer/shared/api/ai-stream-headers'
import { ApiHttpService } from '@flowx/designer/shared/api/api-http.service'

import {
  AddThreadMessageRequest,
  AddThreadMessageResponse,
  AddThreadRequest,
  AddThreadResponse,
  AppendOutcomeRequest,
  AppendOutcomeResponse,
  Conversation,
  CreateConversationRequest,
  CreateConversationResponse,
  DiscardThreadOutcomeRequest,
  KeepThreadOutcomeRequest,
  KeepThreadOutcomeResponse,
  PaginatedConversations,
  PaginatedMessages,
  PatchConversationContextRequest,
  PatchMessageContextRequest,
  PatchPlanActionRequest,
  PatchPlanActionResponse,
  PatchThreadContextRequest,
  ReplaceMessageExecutionPlanRequest,
  StreamMessageRequest,
} from './conversation-api.types'
import { ActionProgressEvent } from './conversation-stream.types'

const BASE_PATH = 'ai/api'
const DEFAULT_PAGE_SIZE = 20

@Injectable({ providedIn: 'root' })
export class ConversationApiService extends ApiHttpService {
  constructor() {
    const baseUrl = inject<string>('BASE_URL' as any)
    const httpClient = inject(HttpClient)
    super(baseUrl, httpClient)
  }

  // ─── Conversations ──────────────────────────────────────────────────────────

  createConversation(payload: CreateConversationRequest): Observable<CreateConversationResponse> {
    return this.post(`${BASE_PATH}/conversations`, payload)
  }

  fetchConversation(conversationId: string): Observable<Conversation> {
    return this.get(`${BASE_PATH}/conversations/${conversationId}`)
  }

  listConversations(page = 0, size = DEFAULT_PAGE_SIZE): Observable<PaginatedConversations> {
    return this.get(`${BASE_PATH}/conversations`, { params: { page, size } })
  }

  listMessagesByConversation(
    conversationId: string,
    page = 0,
    size = DEFAULT_PAGE_SIZE
  ): Observable<PaginatedMessages> {
    return this.get(`${BASE_PATH}/conversations/${conversationId}/messages`, {
      params: { page, size },
    })
  }

  closeConversation(conversationId: string): Observable<void> {
    return this.delete(`${BASE_PATH}/conversations/${conversationId}`)
  }

  patchConversationContext(
    conversationId: string,
    payload: PatchConversationContextRequest
  ): Observable<void> {
    return this.patch(`${BASE_PATH}/conversations/${conversationId}/context`, payload)
  }

  // ─── Threads ────────────────────────────────────────────────────────────────

  addThread(conversationId: string, payload: AddThreadRequest): Observable<AddThreadResponse> {
    return this.post(`${BASE_PATH}/conversations/${conversationId}/threads`, payload)
  }

  keepThread(
    conversationId: string,
    threadId: string,
    payload: KeepThreadOutcomeRequest
  ): Observable<KeepThreadOutcomeResponse> {
    return this.post(
      `${BASE_PATH}/conversations/${conversationId}/threads/${threadId}/keep`,
      payload
    )
  }

  discardThread(
    conversationId: string,
    threadId: string,
    payload: DiscardThreadOutcomeRequest
  ): Observable<void> {
    return this.post(
      `${BASE_PATH}/conversations/${conversationId}/threads/${threadId}/discard`,
      payload
    )
  }

  patchThreadContext(
    conversationId: string,
    threadId: string,
    payload: PatchThreadContextRequest
  ): Observable<void> {
    return this.patch(
      `${BASE_PATH}/conversations/${conversationId}/threads/${threadId}/context`,
      payload
    )
  }

  // ─── Messages ───────────────────────────────────────────────────────────────

  addThreadMessage(
    conversationId: string,
    threadId: string,
    payload: AddThreadMessageRequest
  ): Observable<AddThreadMessageResponse> {
    return this.post(
      `${BASE_PATH}/conversations/${conversationId}/threads/${threadId}/messages`,
      payload
    )
  }

  patchMessageContext(
    conversationId: string,
    threadId: string,
    messageId: string,
    payload: PatchMessageContextRequest
  ): Observable<void> {
    return this.patch(
      `${BASE_PATH}/conversations/${conversationId}/threads/${threadId}/messages/${messageId}/context`,
      payload
    )
  }

  patchPlanAction(
    conversationId: string,
    threadId: string,
    messageId: string,
    actionLocalId: string,
    payload: PatchPlanActionRequest
  ): Observable<PatchPlanActionResponse> {
    return this.patch(
      `${BASE_PATH}/conversations/${conversationId}/threads/${threadId}/messages/${messageId}/plan/actions/${actionLocalId}`,
      payload
    )
  }

  replaceMessagePlan(
    conversationId: string,
    threadId: string,
    messageId: string,
    payload: ReplaceMessageExecutionPlanRequest
  ): Observable<void> {
    return this.put(
      `${BASE_PATH}/conversations/${conversationId}/threads/${threadId}/messages/${messageId}/plan`,
      payload
    )
  }

  appendOutcome(
    conversationId: string,
    threadId: string,
    messageId: string,
    payload: AppendOutcomeRequest
  ): Observable<AppendOutcomeResponse> {
    return this.post(
      `${BASE_PATH}/conversations/${conversationId}/threads/${threadId}/messages/${messageId}/outcomes`,
      payload
    )
  }

  // ─── Streaming (SSE) ────────────────────────────────────────────────────────

  // Server emits three named SSE events: `progress` (incremental updates),
  // `terminal` (clean end-of-stream), `error` (stream-side failure).
  // Reconnect uses `Last-Event-ID`; EventSourcePolyfill handles that for us.
  // TO CHECK: confirm stream events are received and dispatched into the
  // reducer for all flows (default thread, agent thread, hint-started action).
  streamMessage(
    payload: StreamMessageRequest,
    workspaceId: string | null,
    appId: string | null
  ): Observable<ActionProgressEvent> {
    return new Observable<ActionProgressEvent>((subscriber) => {
      const url = this.buildUrl(
        `${BASE_PATH}/conversations/${payload.conversationId}/threads/${payload.threadId}/messages/${payload.messageId}/stream`
      )
      const eventSource = new EventSourcePolyfill(url, {
        headers: aiStreamHeaders(workspaceId, appId),
        heartbeatTimeout: 9000000,
        withCredentials: true,
      })

      // EventSourcePolyfill dispatches a generic 'error' event whenever the
      // underlying fetch/XHR finishes — including a clean server-initiated
      // close (the spec EventSource auto-reconnects, so any close looks like
      // an error). The polyfill distinguishes the two by attaching the
      // original `Error` on `event.error` for real failures, and leaving it
      // `null` for a natural EOF. We treat the latter as completion so
      // stream paths that don't emit a `terminal` frame don't false-error.
      let completed = false

      const onProgress = (event: MessageEvent): void => {
        try {
          subscriber.next(JSON.parse(event.data) as ActionProgressEvent)
        } catch (error) {
          // Malformed frames must not tear down the stream — erroring the
          // subscriber would run teardown and close the EventSource, ending
          // all subsequent events. Skip the frame and keep listening.
          console.warn('[ConversationApi] dropped unparseable progress frame', {
            data: event.data,
            error,
          })
        }
      }
      const onTerminal = (event: MessageEvent): void => {
        // The terminal frame carries the final cumulative snapshot (e.g. the
        // MAIN_OUTCOME). Emit it before completing so the store applies it —
        // otherwise the outcome only lands via the post-stream refetch and any
        // stream-success consumer reads an empty outcome.
        if (typeof event?.data === 'string' && event.data.length > 0) {
          try {
            subscriber.next(JSON.parse(event.data) as ActionProgressEvent)
          } catch (error) {
            console.warn('[ConversationApi] dropped unparseable terminal frame', {
              data: event.data,
              error,
            })
          }
        }
        completed = true
        subscriber.complete()
      }
      const onErrorEvent = (event: MessageEvent): void => {
        if (completed) {
          return
        }
        // The polyfill funnels three different events through the 'error' slot:
        //   1. Server-sent `event: error` frame → MessageEvent with `.data`.
        //   2. Connection-start failure (non-200 / wrong content-type) →
        //      ConnectionEvent with `.status` (and `.statusText`).
        //   3. Transport onFinish → ErrorEvent with `.error` (null on clean EOF,
        //      Error on real failure).
        // Only case 3 with `error == null` is a clean stream end; everything
        // else gets propagated downstream.
        const errorEvent = event as unknown as {
          data?: unknown
          status?: number
          statusText?: string
          error?: unknown
        }
        if (typeof errorEvent.data === 'string' && errorEvent.data.length > 0) {
          let parsed: unknown
          try {
            parsed = JSON.parse(errorEvent.data)
          } catch {
            parsed = new Error(errorEvent.data)
          }
          subscriber.error(parsed)
          return
        }
        if (typeof errorEvent.status === 'number') {
          subscriber.error({
            status: errorEvent.status,
            statusText: errorEvent.statusText,
            message: `SSE connection failed with status ${errorEvent.status}${
              errorEvent.statusText ? ` ${errorEvent.statusText}` : ''
            }`,
          })
          return
        }
        if (errorEvent.error == null) {
          completed = true
          subscriber.complete()
          return
        }
        subscriber.error(errorEvent.error)
      }
      const onTransportError = (error: Event): void => {
        if (completed) {
          return
        }
        const transportError = (error as unknown as { error?: unknown }).error
        if (transportError == null) {
          completed = true
          subscriber.complete()
          return
        }
        subscriber.error(error)
      }

      eventSource.addEventListener('progress', onProgress as EventListener)
      eventSource.addEventListener('terminal', onTerminal as EventListener)
      eventSource.addEventListener('error', onErrorEvent as EventListener)
      // EventSource also fires a generic 'error' on transport failure (different
      // event type from the named 'error' frame above). Polyfill dispatches both
      // through the same listener slot — we treat either as an error.
      eventSource.onerror = onTransportError

      return () => {
        eventSource.close()
      }
    })
  }
}
